// frontend/src/components/Navbar.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import notificationAxios from "../notificationAxios";

/* ───────────────────── Icons ───────────────────── */
const HamburgerIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
  </svg>
);
const CloseIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const SunIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <circle cx="12" cy="12" r="4" strokeWidth="2" />
    <path strokeWidth="2" d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);
const MoonIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeWidth="2" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
  </svg>
);

export default function Navbar({
  user,
  onLogout,
  unread,
  setUnread,
  showNotifDropdown,
  setShowNotifDropdown,
  notifications,
  fetchNotifications,
  showProfileDropdown,
  setShowProfileDropdown,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  /* ───────── State ───────── */
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [tradeOpen, setTradeOpen] = useState(false);

  const [notifPinned, setNotifPinned] = useState(false);
  const [profilePinned, setProfilePinned] = useState(false);
  const [tradePinned, setTradePinned] = useState(false);

  const [dropdownOpen, setDropdownOpen] = useState(null);

  // Theme state
  const [theme, setTheme] = useState("light"); // 'light' | 'dark'

  /* ───────── Refs ───────── */
  const notifDesktopRef = useRef(null);
  const tradeDesktopRef = useRef(null);
  const profileDesktopRef = useRef(null);

  const notifMobileRef = useRef(null);
  const mobileControlsRef = useRef(null);
  const mobileMenuPanelRef = useRef(null);

  // Hover close timers
  const notifHoverTimerRef = useRef(null);
  const tradeHoverTimerRef = useRef(null);
  const profileHoverTimerRef = useRef(null);

  // Mobile notif list height
  const MOBILE_VISIBLE_ROWS = 6;
  const MOBILE_ROW_EST_PX = 48;
  const MOBILE_LIST_MAX_PX = MOBILE_VISIBLE_ROWS * MOBILE_ROW_EST_PX;

  const HOVER_MIN_MS = 1500;

  /* ───────── Theme init & persist ───────── */
  useEffect(() => {
    const stored = localStorage.getItem("theme");
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const initial = stored || (prefersDark ? "dark" : "light");
    setTheme(initial);
    document.documentElement.classList.toggle("dark", initial === "dark");
  }, []);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("theme", next);
    document.documentElement.classList.toggle("dark", next === "dark");
  };

  /* ───────── Hover helpers ───────── */
  const openNotif = () => {
    clearTimeout(notifHoverTimerRef.current);
    setShowNotifDropdown(true);
  };
  const leaveNotif = () => {
    if (notifPinned) return;
    clearTimeout(notifHoverTimerRef.current);
    notifHoverTimerRef.current = setTimeout(() => {
      if (!notifPinned) setShowNotifDropdown(false);
    }, HOVER_MIN_MS);
  };

  const openTrade = () => {
    clearTimeout(tradeHoverTimerRef.current);
    setTradeOpen(true);
  };
  const leaveTrade = () => {
    if (tradePinned) return;
    clearTimeout(tradeHoverTimerRef.current);
    tradeHoverTimerRef.current = setTimeout(() => {
      if (!tradePinned) setTradeOpen(false);
    }, HOVER_MIN_MS);
  };

  const openProfile = () => {
    clearTimeout(profileHoverTimerRef.current);
    setShowProfileDropdown(true);
  };
  const leaveProfile = () => {
    if (profilePinned) return;
    clearTimeout(profileHoverTimerRef.current);
    profileHoverTimerRef.current = setTimeout(() => {
      if (!profilePinned) setShowProfileDropdown(false);
    }, HOVER_MIN_MS);
  };

  /* ───────── Close on route change ───────── */
  useEffect(() => {
    setBurgerOpen(false);
    setShowNotifDropdown(false);
    setShowProfileDropdown(false);
    setTradeOpen(false);
    setNotifPinned(false);
    setProfilePinned(false);
    setTradePinned(false);
  }, [location.pathname, setShowNotifDropdown, setShowProfileDropdown]);

  /* ───────── Click-outside + Esc ───────── */
  useEffect(() => {
    const handleDocClick = (e) => {
      const inNotifDesktop =
        notifDesktopRef.current && notifDesktopRef.current.contains(e.target);
      const inTradeDesktop =
        tradeDesktopRef.current && tradeDesktopRef.current.contains(e.target);
      const inProfileDesktop =
        profileDesktopRef.current && profileDesktopRef.current.contains(e.target);

      const inNotifMobile =
        notifMobileRef.current && notifMobileRef.current.contains(e.target);
      const inMobileMenuControls =
        mobileControlsRef.current && mobileControlsRef.current.contains(e.target);
      const inMobileMenuPanel =
        mobileMenuPanelRef.current && mobileMenuPanelRef.current.contains(e.target);

      if (showNotifDropdown && !inNotifDesktop && !inNotifMobile) {
        setNotifPinned(false);
        setShowNotifDropdown(false);
      }
      if (tradeOpen && !inTradeDesktop) {
        setTradePinned(false);
        setTradeOpen(false);
      }
      if (showProfileDropdown && !inProfileDesktop) {
        setProfilePinned(false);
        setShowProfileDropdown(false);
      }

      const inMobileMenu = inMobileMenuControls || inMobileMenuPanel;
      if (burgerOpen && !inMobileMenu) {
        setBurgerOpen(false);
      }
    };

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setShowNotifDropdown(false);
        setTradeOpen(false);
        setShowProfileDropdown(false);
        setNotifPinned(false);
        setTradePinned(false);
        setProfilePinned(false);
        setBurgerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocClick);
    document.addEventListener("touchstart", handleDocClick, { passive: true });
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleDocClick);
      document.removeEventListener("touchstart", handleDocClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [showNotifDropdown, tradeOpen, showProfileDropdown, burgerOpen]);

  /* ───────── Links ───────── */
  const links = [
    { to: "/dashboard", label: "Dashboard", protected: true },
    { to: "/wallet", label: "Wallet", protected: true },
    { to: "/market", label: "Market", protected: false },
    { to: "/about", label: "About Us", protected: false },
    { to: "/support", label: "Support", protected: false },
    {
      dropdown: true,
      label: "Trade",
      protected: true,
      children: [
        { to: "/quick-trade", label: "Quick Trade" },
        { to: "/exchange", label: "Exchange" },
        { to: "/p2p", label: "P2P Market" },
      ],
    },
  ];
  const visibleLinks = links.filter((l) => !l.protected || user);

  return (
    <header className="bg-white dark:bg-slate-900 shadow sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex h-16 items-center">
        {/* Logo (left) */}
        <div
          className="flex-shrink-0 flex items-center cursor-pointer"
          onClick={() => navigate("/")}
        >
          <img className="h-10 w-10" src="/logo.png" alt="Coinzetto Logo" />
          <span className="ml-2 text-3xl font-bold text-blue-600 dark:text-blue-400">
            Coinzetto
          </span>
        </div>

        <div className="flex-1" />

        {/* Desktop nav (center-right) */}
        <div className="hidden md:flex md:items-center md:space-x-4">
          {visibleLinks.map((link) =>
            link.dropdown ? (
              <div
                key={link.label}
                className="relative"
                ref={tradeDesktopRef}
                onMouseEnter={openTrade}
                onMouseLeave={leaveTrade}
              >
                <button
                  type="button"
                  className="px-3 py-2 rounded-lg text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition"
                  onClick={() => {
                    setTradePinned(true);
                    setTradeOpen(true);
                    setShowNotifDropdown(false);
                    setNotifPinned(false);
                    setShowProfileDropdown(false);
                    setProfilePinned(false);
                  }}
                >
                  {link.label}
                </button>
                {tradeOpen && (
                  <div className="absolute mt-2 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-md rounded-lg z-50">
                    {link.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700/60"
                        onClick={() => {
                          setTradeOpen(false);
                          setTradePinned(false);
                        }}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                className="px-3 py-2 rounded-lg text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition"
              >
                {link.label}
              </Link>
            )
          )}

          {user ? (
            <div className="relative flex items-center space-x-4">
              {/* 🔔 Desktop bell */}
              <div
                className="relative"
                ref={notifDesktopRef}
                onMouseEnter={openNotif}
                onMouseLeave={leaveNotif}
              >
                <button
                  onClick={() => {
                    setShowNotifDropdown(true);
                    setNotifPinned(true);
                    setShowProfileDropdown(false);
                    setProfilePinned(false);
                    setTradeOpen(false);
                    setTradePinned(false);
                  }}
                  className="relative p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 focus:outline-none"
                  aria-expanded={showNotifDropdown}
                  aria-haspopup="menu"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-gray-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 17h5l-1.405-1.405A2 2 0 0118 14.158V11a6 6 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                  {unread.length > 0 && (
                    <span className="absolute top-0 right-0 -mt-1 -mr-1 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                      {unread.length}
                    </span>
                  )}
                </button>

                {showNotifDropdown && (
                  <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-md z-50 max-h-80 overflow-y-auto flex flex-col">
                    <div className="p-2 text-gray-700 dark:text-slate-200 font-semibold border-b border-gray-200 dark:border-slate-700">
                      Notifications
                    </div>

                    {notifications.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-slate-400 px-4 py-2">
                        No notifications yet.
                      </p>
                    ) : (
                      notifications.map((item) => (
                        <div
                          key={item._id}
                          className={`block px-4 py-2 text-sm cursor-pointer ${
                            item.read
                              ? "text-gray-500 dark:text-slate-400"
                              : "text-gray-800 dark:text-slate-100 font-semibold"
                          } hover:bg-gray-100 dark:hover:bg-slate-700/60`}
                          onClick={() => {
                            notificationAxios
                              .post(`/notifications/mark-read`, {
                                tradeId: item.tradeId,
                                type: item.type,
                              })
                              .then(() => {
                                fetchNotifications();
                                if (location.pathname === `/p2p/trade/${item.tradeId}`) {
                                  navigate("/p2p");
                                  setTimeout(() => navigate(`/p2p/trade/${item.tradeId}`), 10);
                                } else {
                                  navigate(item.url || "/");
                                }
                                setShowNotifDropdown(false);
                                setNotifPinned(false);
                              });
                          }}
                        >
                          <span className="font-semibold">{item.type}:</span> {item.message}
                          <div className="text-xs text-gray-400 dark:text-slate-400">
                            {new Date(item.createdAt).toLocaleString()}
                          </div>
                        </div>
                      ))
                    )}

                    {/* Sticky footer */}
                    <div className="border-t border-gray-200 dark:border-slate-700 sticky bottom-0 bg-white dark:bg-slate-800">
                      <button
                        className="w-full text-center py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-slate-700/60"
                        onClick={() => {
                          notificationAxios.post(`/notifications/mark-read`).then(() => {
                            setUnread && setUnread([]);
                            fetchNotifications();
                          });
                        }}
                      >
                        Mark all as read
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* 👤 Desktop profile */}
              <div
                className="relative"
                ref={profileDesktopRef}
                onMouseEnter={openProfile}
                onMouseLeave={leaveProfile}
              >
                <button
                  className="h-9 w-9 bg-indigo-100 dark:bg-indigo-900/40 rounded-full flex items-center justify-center cursor-pointer hover:bg-indigo-200 dark:hover:bg-indigo-800/60 transition"
                  onClick={() => {
                    setShowProfileDropdown(true);
                    setProfilePinned(true);
                    setShowNotifDropdown(false);
                    setNotifPinned(false);
                    setTradeOpen(false);
                    setTradePinned(false);
                  }}
                  aria-expanded={showProfileDropdown}
                  aria-haspopup="menu"
                >
                  <span className="text-indigo-600 dark:text-indigo-300 font-bold">
                    {user?.firstName?.charAt(0).toUpperCase()}
                  </span>
                </button>

                {showProfileDropdown && (
                  <div className="absolute right-0 mt-2 w-44 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-md z-50">
                    <button
                      onClick={() => {
                        navigate("/dashboard");
                        setShowProfileDropdown(false);
                        setProfilePinned(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700/60"
                    >
                      Dashboard
                    </button>
                    <button
                      onClick={() => {
                        navigate("/profile");
                        setShowProfileDropdown(false);
                        setProfilePinned(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700/60"
                    >
                      My Profile
                    </button>
                    <button
                      onClick={() => {
                        navigate("/settings/notifications");
                        setShowProfileDropdown(false);
                        setProfilePinned(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700/60"
                    >
                      Settings
                    </button>
                    <button
                      onClick={() => {
                        onLogout();
                        setShowProfileDropdown(false);
                        setProfilePinned(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700/60"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <Link
                to="/login"
                className="px-3 py-2 rounded-lg text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition"
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="px-3 py-2 rounded-lg text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition"
              >
                Signup
              </Link>
            </>
          )}
        </div>

        {/* Desktop theme toggle pinned at the far right corner */}
        <div className="hidden md:block ml-3">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 focus:outline-none"
            aria-label="Toggle theme"
            title={theme === "dark" ? "Light mode" : "Dark mode"}
          >
            {theme === "dark" ? (
              <SunIcon className="h-5 w-5 text-yellow-400" />
            ) : (
              <MoonIcon className="h-5 w-5 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile: bell + tiny hamburger (right edge) — NO theme button here */}
        <div className="flex md:hidden items-center gap-2 relative" ref={mobileControlsRef}>
          {/* 🔔 Bell (mobile) */}
          {user && (
            <div className="relative" ref={notifMobileRef}>
              <button
                onClick={() => {
                  setShowNotifDropdown((s) => !s);
                  setShowProfileDropdown(false);
                }}
                className="relative p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 focus:outline-none"
                aria-label="Notifications"
                aria-expanded={showNotifDropdown}
                aria-haspopup="menu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 17h5l-1.405-1.405A2 2 0 0118 14.158V11a6 6 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                {unread.length > 0 && (
                  <span className="absolute top-0 right-0 -mt-0.5 -mr-0.5 bg-red-500 text-white text-[10px] font-bold min-w-[16px] h-[16px] leading-[16px] px-1 rounded-full text-center">
                    {unread.length}
                  </span>
                )}
              </button>

              {/* Bell dropdown (mobile) */}
              {showNotifDropdown && (
                <div className="absolute right-0 top-9 w-64 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-md z-50 max-h-[70vh] flex flex-col">
                  <div className="px-3 py-2 text-gray-700 dark:text-slate-200 font-semibold border-b border-gray-200 dark:border-slate-700 text-xs">
                    Notifications
                  </div>
                  <div
                    className="grow overflow-y-auto divide-y divide-gray-200 dark:divide-slate-700"
                    style={{ maxHeight: `${MOBILE_LIST_MAX_PX}px` }}
                  >
                    {notifications.length === 0 ? (
                      <p className="text-[12px] text-gray-500 dark:text-slate-400 px-3 py-2">
                        No notifications yet.
                      </p>
                    ) : (
                      notifications.map((item) => (
                        <div
                          key={item._id}
                          className={`px-3 py-2 text-[12px] cursor-pointer ${
                            item.read
                              ? "text-gray-500 dark:text-slate-400"
                              : "text-gray-800 dark:text-slate-100 font-semibold"
                          } hover:bg-gray-50 dark:hover:bg-slate-700/60`}
                          onClick={() => {
                            notificationAxios
                              .post(`/notifications/mark-read`, {
                                tradeId: item.tradeId,
                                type: item.type,
                              })
                              .then(() => {
                                fetchNotifications();
                                navigate(item.url || "/");
                                setShowNotifDropdown(false);
                              });
                          }}
                        >
                          <span className="font-semibold">{item.type}:</span> {item.message}
                          <div className="text-[10px] text-gray-400 dark:text-slate-400">
                            {new Date(item.createdAt).toLocaleString()}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {/* Sticky footer */}
                  <div className="shrink-0 sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-2">
                    <button
                      className="w-full text-center py-2 text-xs text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:scale-[0.99]"
                      onClick={() => {
                        notificationAxios.post(`/notifications/mark-read`).then(() => {
                          setUnread && setUnread([]);
                          fetchNotifications();
                        });
                      }}
                    >
                      Mark all as read
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 🍔 Tiny hamburger */}
          <div className="relative">
            <button
              onClick={() => setBurgerOpen((s) => !s)}
              className="p-1 rounded-md text-gray-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-800 focus:outline-none"
              aria-expanded={burgerOpen}
              aria-haspopup="menu"
              aria-label="Menu"
            >
              {burgerOpen ? <CloseIcon className="h-4 w-4" /> : <HamburgerIcon className="h-4 w-4" />}
            </button>

            {burgerOpen && (
              <div
                ref={mobileMenuPanelRef}
                className="absolute right-0 top-9 w-56 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg z-50 py-2"
                role="menu"
              >
                <nav className="text-sm">
                  {visibleLinks.map((link, idx) =>
                    link.dropdown ? (
                      <div key={idx} className="px-2">
                        <button
                          onClick={() =>
                            setDropdownOpen((v) => (v === link.label ? null : link.label))
                          }
                          className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700/60 text-gray-700 dark:text-slate-200"
                        >
                          {link.label}
                        </button>
                        {dropdownOpen === link.label && (
                          <div className="ml-2">
                            {link.children.map((child) => (
                              <Link
                                key={child.to}
                                to={child.to}
                                onClick={() => setBurgerOpen(false)}
                                className="block px-4 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700/60 text-gray-700 dark:text-slate-200"
                              >
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setBurgerOpen(false)}
                        className="block px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/60 text-gray-700 dark:text-slate-200"
                      >
                        {link.label}
                      </Link>
                    )
                  )}

                  {user ? (
                    <>
                      <Link
                        to="/profile"
                        onClick={() => setBurgerOpen(false)}
                        className="block px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/60 text-gray-700 dark:text-slate-200"
                      >
                        My Profile
                      </Link>
                      <Link
                        to="/settings/notifications"
                        onClick={() => setBurgerOpen(false)}
                        className="block px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/60 text-gray-700 dark:text-slate-200"
                      >
                        Settings
                      </Link>
                      <button
                        onClick={() => {
                          onLogout();
                          setBurgerOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/60 text-gray-700 dark:text-slate-200"
                      >
                        Logout
                      </button>

                      {/* Theme toggle (text only) below Logout */}
                      <button
                        onClick={toggleTheme}
                        className="mt-1 w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/60 text-gray-700 dark:text-slate-200"
                      >
                        {theme === "dark" ? "Light mode" : "Dark mode"}
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        onClick={() => setBurgerOpen(false)}
                        className="block px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/60 text-gray-700 dark:text-slate-200"
                      >
                        Login
                      </Link>
                      <Link
                        to="/signup"
                        onClick={() => setBurgerOpen(false)}
                        className="block px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/60 text-gray-700 dark:text-slate-200"
                      >
                        Signup
                      </Link>

                      {/* Theme toggle (text only) below auth links */}
                      <button
                        onClick={toggleTheme}
                        className="mt-1 w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/60 text-gray-700 dark:text-slate-200"
                      >
                        {theme === "dark" ? "Light mode" : "Dark mode"}
                      </button>
                    </>
                  )}
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* NOTE: no full-width mobile menu; we use the small right popover */}
    </header>
  );
}
